import * as React from 'react';
import styled from 'styled-components';
import gettext from '@sdk/lang/gettext';
import Text14 from '@sdk/components/ui/typography/Text14';
import TextField from '@sdk/components/forms/TextField';
import useAuth from '@sdk/authentication/useAuth';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import type { UpdateLaborCostSettings } from '@sdk/labor/types';
import Column from '@sdk/components/ui/spacing/Column';

import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import { FIELD_WIDTH_205 } from '../../constants';
import { TabType } from '../../types';
import { useSettingsErrorsContext } from '../../SettingsErrorContext';

const StyledTextField = styled(TextField)`
  max-width: ${FIELD_WIDTH_205}px;
`;

type Props = {
  spreadOfHoursPay: number | string;
  spreadOfHoursThreshold: number | string;
  spreadOfHoursEnabled: boolean;
  onLaborCostSettingsChange: UpdateLaborCostSettings;
};

const SpreadOfHours: React.FC<React.PropsWithChildren<Props>> = ({
  onLaborCostSettingsChange,
  spreadOfHoursPay,
  spreadOfHoursThreshold,
  spreadOfHoursEnabled,
}) => {
  const { toggleError } = useSettingsErrorsContext();
  const { currentUser } = useAuth();

  const validateSpreadOfHoursPay = (value: number | string): boolean => {
    return validateNumber(value, {
      [ValidateOptions.validNumber]: ValidNumberOption.positive,
    });
  };

  const validateSpreadOfHoursThreshold = (value: number | string): boolean => {
    return validateNumber(value, {
      [ValidateOptions.validNumber]: ValidNumberOption.positive,
    });
  };

  const onSpreadOfHoursChange = (value: boolean) => {
    onLaborCostSettingsChange({ spreadOfHoursEnabled: value });

    if (value) {
      toggleError(
        TabType.laborCost,
        'spreadOfHoursThreshold',
        !validateSpreadOfHoursThreshold(spreadOfHoursThreshold)
      );

      toggleError(
        TabType.laborCost,
        'spreadOfHoursPay',
        !validateSpreadOfHoursPay(spreadOfHoursPay)
      );
    } else {
      toggleError(TabType.laborCost, 'spreadOfHoursThreshold', false);

      toggleError(TabType.laborCost, 'spreadOfHoursPay', false);
    }
  };

  return (
    <CollapsibleSettingsSection
      pBottom={20}
      withSeparator
      isActive={spreadOfHoursEnabled}
      headerItem={
        <SettingsSwitcherRow
          subtitle={gettext('Enable extra hour of pay for longer workdays.')}
          value={spreadOfHoursEnabled}
          onChange={(value: boolean): void => {
            onSpreadOfHoursChange(value);
          }}
          disabled={!currentUser.isAdmin}
        />
      }
      contentItem={
        <>
          <RowSpaceBetween>
            <Column>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                {gettext('Time frame')}
              </Text14>
              <Text14 themeColor={ThemeColorType.gray}>
                {gettext('Specify the rule for the spread of hours compensation.')}
              </Text14>
            </Column>
            <StyledTextField
              skipErrorMessage
              type="number"
              disabled={!currentUser.isAdmin}
              postfix={'Hours'}
              placeholder="0"
              value={spreadOfHoursThreshold}
              onChange={(event): void => {
                onLaborCostSettingsChange({
                  spreadOfHoursThreshold: event.currentTarget.value,
                });
                toggleError(
                  TabType.laborCost,
                  'spreadOfHoursThreshold',
                  !validateSpreadOfHoursThreshold(event.currentTarget.value)
                );
              }}
              invalid={!validateSpreadOfHoursThreshold(spreadOfHoursThreshold)}
            />
          </RowSpaceBetween>
          <RowSpaceBetween mTop={20}>
            <Column>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                {gettext('Pay rate')}
              </Text14>
              <Text14 themeColor={ThemeColorType.gray}>
                {gettext('Specify the rate for the spread of hours compensation.')}
              </Text14>
            </Column>
            <StyledTextField
              skipErrorMessage
              type="number"
              disabled={!currentUser.isAdmin}
              postfix={'$'}
              value={spreadOfHoursPay}
              onChange={(event): void => {
                onLaborCostSettingsChange({
                  spreadOfHoursPay: event.currentTarget.value,
                });
                toggleError(
                  TabType.laborCost,
                  'spreadOfHoursPay',
                  !validateSpreadOfHoursPay(event.currentTarget.value)
                );
              }}
              invalid={!validateSpreadOfHoursPay(spreadOfHoursPay)}
            />
          </RowSpaceBetween>
        </>
      }
    />
  );
};

export default SpreadOfHours;
